// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the change from a held baseline, drawn on the globe
 * (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, slice 2; owner, 2026-10-05:
 * "a feature that shows the % change on the globe").
 *
 * The baseline frame's luma plane is kept whole (`Baseline.snapshot`);
 * this compares it texel by texel with the frame on screen and paints the
 * result on a global equirectangular grid, which the globe draws as a
 * layer just above the dataset. Two readings:
 *
 * - **Change**: now minus baseline, in the dataset's units. Right for
 *   anything that can be zero or negative (temperature anomalies, sea
 *   level change).
 * - **% change**: (now − baseline) / |baseline| × 100. Right for positive
 *   quantities (rain, aerosol optical depth, sea ice concentration).
 *   Where the baseline is near zero a percentage means nothing (2 mm/h
 *   over 0.01 mm/h is +19,900 %), so texels whose |baseline| is under
 *   `percentFloor` are left empty, never painted in the extreme colour.
 *
 * Honest by construction: a texel with no data in either frame stays
 * empty; the colour range is symmetric about zero (no change is always
 * white), and set from the 98th percentile of |change| so one outlier
 * cannot wash the map out; the legend says what was masked. Pure: no DOM.
 */

import { latLonToTexelUv } from '../datasetProbe'
import type { LumaSnapshot } from '../glLumaSampler'
import { isTransparentLuma, lumaToValue, type ColorScale } from '../../types/color-scale'
import type { DatasetOverlayOptions } from '../../types'
import { PALETTE_IDS, paletteStops, type PaletteId } from '../colorScaleDisplay'
import { paletteAt } from '../dashRelease'
import { SIGMA_LIMIT, cellOf, decodeMean, decodeSigma, meanMagnitudes, periodChange, sampledTexel, type Climatology } from './climatology'

/**
 * AYNI: `sigma` is the standardised change, (value − mean) / σ, against a
 * period's climatology only (`climatology.ts`): one frame has no σ.
 */
export type ChangeMode = 'difference' | 'percent' | 'sigma'

/** The grid the change is painted on: global, 2:1, as the layer pass expects. */
export const CHANGE_GRID_WIDTH = 2048
export const CHANGE_GRID_HEIGHT = 1024

export interface ChangeGrid {
  readonly width: number
  readonly height: number
  /** Row-major from the north-west corner; NaN where there is nothing to show. */
  readonly values: Float32Array
  /**
   * 1 where there is data but no meaningful change: one frame has data and
   * the other none, or a percentage over a near-zero baseline. Painted grey,
   * so the dataset underneath can never be read as part of the change map.
   */
  readonly grey: Uint8Array
  readonly mode: ChangeMode
  /** The symmetric colour limit: ±limit spans the palette. */
  readonly limit: number
  /** AYNI: the range painted: the viewer's own when they set one, else ±limit. */
  readonly range: ChangeRange
  /** Texels left empty because the baseline was too close to zero for a percentage. */
  readonly masked: number
  /** The |baseline| under which a percentage is not shown (percent mode). */
  readonly floor: number | null
  /** Texels painted. */
  readonly painted: number
  /** Percent mode: texels whose baseline is below zero (a percentage of a negative value misleads). */
  readonly negative: number
}

/**
 * Whether % change is the wrong reading for this field: its baseline has
 * values below zero, or a quarter of it had to be left out as near zero
 * (an anomaly, a temperature in °C). "Change" is the reading to use then.
 */
export function percentMisleads(grid: ChangeGrid): boolean {
  if (grid.mode !== 'percent') return false
  return grid.negative > 0 || grid.masked > 0.25 * (grid.painted + grid.masked)
}

/** Why a percentage cannot mean anything for a field, before anything is drawn. */
export type PercentRefusal = 'interval' | 'negative'

/**
 * A % change is defined only for a ratio-scale quantity, whose zero means
 * none (rain, a concentration, ice extent, kelvin): 4 mm to 6 mm is +50 %.
 * A field that can go below zero (an anomaly, a change, a temperature
 * in °C) has an arbitrary zero, so its percentages depend on where zero
 * happens to sit and explode wherever it passes through it; a
 * temperature in °C or °F is interval-scale even when every value is
 * positive (10 °C to 20 °C is not twice as hot). Read from the scale, so
 * the answer is the same on every frame.
 */
export function percentRefusal(scale: Pick<ColorScale, 'vmin' | 'units'>): PercentRefusal | null {
  // Below zero first: it is the reason a percentage explodes, and the one an anomaly in °C needs.
  if (scale.vmin < 0) return 'negative'
  const units = (scale.units ?? '').trim().toLowerCase()
  if (/^(°|º|deg\s*)?\s*[cf]$|^deg[cf]$|^degrees?[\s_]*(c|f|celsius|fahrenheit)$|^celsius$|^fahrenheit$/.test(units)) return 'interval'
  return null
}

/** Each luma code's value, NaN where the code means no data. */
export function valueTable(scale: ColorScale): Float64Array {
  const out = new Float64Array(256)
  for (let code = 0; code < 256; code++) out[code] = isTransparentLuma(code, scale) ? NaN : lumaToValue(code, scale)
  return out
}

/** A 1-2-2.5-5 step at or above `x`, so the legend reads in round numbers. */
export function niceLimit(x: number): number {
  if (!(x > 0) || !Number.isFinite(x)) return 1
  const p = 10 ** Math.floor(Math.log10(x))
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= x * (1 - 1e-9)) return m * p
  return 10 * p
}

/**
 * AYNI (owner, 2026-10-20: "allow the user to change the min/max change
 * values"): the change's colour range, its two ends. Always min < 0 < max,
 * so no change stays white; each side is scaled to its own end, so the
 * ends can differ (−0.5 to +2 ppm puts the darkest blue at −0.5 and the
 * darkest red at +2).
 */
export interface ChangeRange {
  readonly min: number
  readonly max: number
}

/** ±limit as a range. */
export function symmetricRange(limit: number): ChangeRange {
  return { min: -limit, max: limit }
}

/** A range a viewer may set: both ends finite, below zero and above zero. */
export function validRange(range: ChangeRange | null | undefined): range is ChangeRange {
  return !!range && Number.isFinite(range.min) && Number.isFinite(range.max) && range.min < 0 && range.max > 0
}

/** Where a change sits on the palette, −1..+1 (not clamped): each side over its own end. */
export function changeT(v: number, range: number | ChangeRange): number {
  const r = typeof range === 'number' ? symmetricRange(range) : range
  return v < 0 ? v / -r.min : v / r.max
}

/** The range to paint: the viewer's when valid, else ±limit. */
function rangeOf(limit: number, range: ChangeRange | null | undefined): ChangeRange {
  return validRange(range) ? range : symmetricRange(limit)
}

/** The q-quantile (0..1) of finite values; NaN when there are none. */
export function quantile(values: ArrayLike<number>, q: number): number {
  const finite: number[] = []
  for (let i = 0; i < values.length; i++) if (Number.isFinite(values[i])) finite.push(values[i])
  if (finite.length === 0) return NaN
  finite.sort((a, b) => a - b)
  const at = Math.min(finite.length - 1, Math.max(0, Math.round(q * (finite.length - 1))))
  return finite[at]
}

/**
 * The |baseline| under which a percentage is left empty: two quantisation
 * steps (a value that small is mostly rounding) or 2 % of the baseline's
 * typical magnitude (its 95th percentile), whichever is larger.
 */
export function percentFloor(baseMagnitudes: ArrayLike<number>, step: number | null): number {
  const typical = quantile(baseMagnitudes, 0.95)
  return Math.max(step !== null && step > 0 ? 2 * step : 0, Number.isFinite(typical) ? 0.02 * typical : 0)
}

/**
 * Which half of {@link percentFloor} set a floor: two quantisation steps
 * (`step` each), or 2 % of the baseline's 95th percentile (`typical`).
 */
export function floorRule(floor: number, step: number | null): { rule: 'steps'; step: number } | { rule: 'typical'; typical: number } {
  if (step !== null && step > 0 && Math.abs(floor - 2 * step) <= 1e-9 * Math.max(1, floor)) return { rule: 'steps', step }
  return { rule: 'typical', typical: floor / 0.02 }
}

export interface ChangeInput {
  readonly now: LumaSnapshot
  readonly base: LumaSnapshot
  readonly nowScale: ColorScale
  readonly baseScale: ColorScale
  /** Where the frame sits on the Earth (bbox, longitude origin, flip, release crop). */
  readonly options?: DatasetOverlayOptions
  readonly mode: ChangeMode
  /** One quantisation step of the baseline, or null where steps are not even (log scales). */
  readonly step: number | null
  readonly width?: number
  readonly height?: number
  /** A colour range fixed earlier (one colour, one amount, in every frame); else set from this frame. */
  readonly limit?: number | null
  /** AYNI: the viewer's own range; null for ±limit. */
  readonly range?: ChangeRange | null
  /** A near-zero threshold fixed earlier; else set from this baseline. */
  readonly floor?: number | null
}

/**
 * The change on the global grid, or null when the two frames are not on
 * the same grid (another resolution: nothing to pair texel by texel).
 *
 * The lon → u and lat → v mappings are separable, so they are found once
 * per column and once per row through `latLonToTexelUv`, the canonical
 * mirror of the shader; the walk is then two table lookups per texel.
 */
/**
 * Which texel of a frame each column and row of the global grid reads
 * (-1: outside the data). The lon → u and lat → v mappings are separable,
 * so they are found once per column and once per row.
 */
export function gridTexels(
  frameWidth: number,
  frameHeight: number,
  options: DatasetOverlayOptions | undefined,
  width: number,
  height: number,
): { cols: Int32Array; rows: Int32Array } {
  const bbox = options?.boundingBox
  const midLat = bbox ? (bbox.n + bbox.s) / 2 : 0
  const midLon = bbox ? (bbox.w <= bbox.e ? (bbox.w + bbox.e) / 2 : ((bbox.w + bbox.e + 360) / 2 + 180) % 360 - 180) : 0
  const cols = new Int32Array(width).fill(-1)
  for (let i = 0; i < width; i++) {
    const uv = latLonToTexelUv(midLat, -180 + ((i + 0.5) * 360) / width, options)
    if (uv) cols[i] = Math.min(frameWidth - 1, Math.max(0, Math.floor(uv.u * frameWidth)))
  }
  const rows = new Int32Array(height).fill(-1)
  for (let j = 0; j < height; j++) {
    const uv = latLonToTexelUv(90 - ((j + 0.5) * 180) / height, midLon, options)
    if (uv) rows[j] = Math.min(frameHeight - 1, Math.max(0, Math.floor(uv.v * frameHeight)))
  }
  return { cols, rows }
}

export function computeChangeGrid(input: ChangeInput): ChangeGrid | null {
  const { now, base } = input
  if (now.width !== base.width || now.height !== base.height || now.width === 0 || now.height === 0) return null
  // AYNI: one frame has no σ (that needs a period), so nothing to draw.
  if (input.mode === 'sigma') return null
  const width = input.width ?? CHANGE_GRID_WIDTH
  const height = input.height ?? CHANGE_GRID_HEIGHT
  const { cols, rows } = gridTexels(now.width, now.height, input.options, width, height)
  const nowValues = valueTable(input.nowScale)
  const baseValues = valueTable(input.baseScale)

  let floor: number | null = input.floor ?? null
  if (input.mode === 'percent' && floor === null) {
    // The baseline's magnitudes, sampled (every 7th texel is plenty for a quantile).
    const sample: number[] = []
    for (let k = 0; k < base.data.length; k += 7) {
      const b = baseValues[base.data[k]]
      if (Number.isFinite(b)) sample.push(Math.abs(b))
    }
    floor = percentFloor(sample, input.step)
  }

  const values = new Float32Array(width * height).fill(NaN)
  const grey = new Uint8Array(width * height)
  let masked = 0
  let painted = 0
  let negative = 0
  for (let j = 0; j < height; j++) {
    const y = rows[j]
    if (y < 0) continue
    const rowOffset = y * now.width
    for (let i = 0; i < width; i++) {
      const x = cols[i]
      if (x < 0) continue
      const a = nowValues[now.data[rowOffset + x]]
      const b = baseValues[base.data[rowOffset + x]]
      const finiteA = Number.isFinite(a)
      const finiteB = Number.isFinite(b)
      if (!finiteA && !finiteB) continue
      if (finiteA !== finiteB) {
        grey[j * width + i] = 1
        continue
      }
      let v = a - b
      if (input.mode === 'percent') {
        if (b < 0) negative++
        if (Math.abs(b) < (floor ?? 0)) {
          masked++
          grey[j * width + i] = 1
          continue
        }
        v = (100 * v) / Math.abs(b)
      }
      values[j * width + i] = v
      painted++
    }
  }
  const magnitudes = new Float32Array(values.length)
  for (let k = 0; k < values.length; k++) magnitudes[k] = Math.abs(values[k])
  const p98 = quantile(magnitudes, 0.98)
  const limit = input.limit !== undefined && input.limit !== null && input.limit > 0 ? input.limit : niceLimit(p98)
  return { width, height, values, grey, mode: input.mode, limit, range: rangeOf(limit, input.range), masked, floor, painted, negative }
}

/**
 * ColorBrewer RdBu, 11 classes, blue (decrease) to red (increase), white
 * at zero: diverging, readable by the common colour-vision deficiencies,
 * and the convention of change maps in climate papers.
 */
export const CHANGE_PALETTE: readonly (readonly [number, number, number])[] = [
  [5, 48, 97], [33, 102, 172], [67, 147, 195], [146, 197, 222], [209, 229, 240],
  [247, 247, 247],
  [253, 219, 199], [244, 165, 130], [214, 96, 77], [178, 24, 43], [103, 0, 31],
]

/**
 * AYNI (owner, 2026-10-20: "add a way to change the color palette"): the
 * change map's colours, chosen in the palette dialog the timeline's
 * palette button opens. `source` is {@link CHANGE_PALETTE} (RdBu); the
 * others are the dataset palettes, their middle at no change. Grayscale is
 * not offered: its middle is the grey that means "no meaningful change".
 */
export interface ChangeColours {
  readonly palette: PaletteId
  /** Run the colours the other way: red for a decrease, blue for an increase. */
  readonly inverted: boolean
}

export const DEFAULT_CHANGE_COLOURS: ChangeColours = { palette: 'source', inverted: false }

/** The palettes the change map can take. */
export const CHANGE_PALETTE_IDS: readonly PaletteId[] = PALETTE_IDS.filter((id) => id !== 'grayscale')

/** Whether these are the default colours (RdBu, not inverted). */
export function defaultChangeColours(colours: ChangeColours | null | undefined): boolean {
  return !colours || (colours.palette === 'source' && !colours.inverted)
}

/** The palette's stops from the low end (−1) to the high end (+1), before any inversion. */
export function changeStops(colours: ChangeColours = DEFAULT_CHANGE_COLOURS): readonly (readonly [number, number, number])[] {
  if (colours.palette === 'source' || !CHANGE_PALETTE_IDS.includes(colours.palette)) return CHANGE_PALETTE
  const stops = paletteStops(colours.palette as Exclude<PaletteId, 'source'>)
  // Even samples, so a stop list reads the same as RdBu's eleven.
  const out: [number, number, number][] = []
  for (let i = 0; i < 11; i++) {
    const [r, g, b] = paletteAt(stops, i / 10)
    out.push([Math.round(r), Math.round(g), Math.round(b)])
  }
  return out
}

/** The palette's stops as the bar draws them, left (−) to right (+), turned round when inverted. */
export function changeBarStops(colours: ChangeColours = DEFAULT_CHANGE_COLOURS): readonly (readonly [number, number, number])[] {
  const stops = changeStops(colours)
  return colours.inverted ? [...stops].reverse() : stops
}

/** The palette's colour at t in [-1, 1] (clamped), interpolated between its stops. */
export function changeColour(t: number, colours: ChangeColours = DEFAULT_CHANGE_COLOURS): [number, number, number] {
  const stops = changeStops(colours)
  const tt = colours.inverted ? -t : t
  const x = ((Math.max(-1, Math.min(1, tt)) + 1) / 2) * (stops.length - 1)
  const i = Math.min(stops.length - 2, Math.floor(x))
  const f = x - i
  const a = stops[i]
  const b = stops[i + 1]
  return [Math.round(a[0] + (b[0] - a[0]) * f), Math.round(a[1] + (b[1] - a[1]) * f), Math.round(a[2] + (b[2] - a[2]) * f)]
}

/** Data without a meaningful change: a neutral grey, never a palette colour. */
export const CHANGE_GREY: readonly [number, number, number] = [128, 128, 128]

/**
 * AYNI: the palette as a table of `steps` colours from −1 to +1, for the
 * period baseline's change on the graphics card (`uClimPalette`): the
 * paused picture paints from the same table through {@link paletteIndex},
 * so both show the same colour for the same amount.
 */
export const PALETTE_STEPS = 1024

export function buildChangePalette(steps = PALETTE_STEPS, colours: ChangeColours = DEFAULT_CHANGE_COLOURS): Uint8Array {
  const out = new Uint8Array(steps * 4)
  for (let i = 0; i < steps; i++) {
    const [r, g, b] = changeColour((2 * i) / (steps - 1) - 1, colours)
    out.set([r, g, b, 255], i * 4)
  }
  return out
}

/** The palette entry for t in [-1, 1] (clamped), as the shader picks it. */
export function paletteIndex(t: number, steps = PALETTE_STEPS): number {
  return Math.floor(((Math.max(-1, Math.min(1, t)) + 1) / 2) * (steps - 1) + 0.5)
}

/**
 * The grid as RGBA pixels: palette colours, grey where there is no meaningful change, transparent where there is no data.
 * AYNI: with `palette` (from {@link buildChangePalette}), colours come from that table, as on the graphics card;
 * else from `colours`, as {@link buildChangeLut} gives them.
 */
export function paintChangeGrid(grid: ChangeGrid, alpha = 235, palette?: Uint8Array, colours: ChangeColours = DEFAULT_CHANGE_COLOURS): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(grid.width * grid.height * 4)
  const steps = palette ? palette.length / 4 : 0
  for (let k = 0; k < grid.values.length; k++) {
    const v = grid.values[k]
    if (grid.grey[k]) {
      out[k * 4] = CHANGE_GREY[0]
      out[k * 4 + 1] = CHANGE_GREY[1]
      out[k * 4 + 2] = CHANGE_GREY[2]
      out[k * 4 + 3] = alpha
      continue
    }
    if (!Number.isFinite(v)) continue
    let r: number, g: number, b: number
    const tv = changeT(v, grid.range)
    if (palette) {
      const i = paletteIndex(tv, steps) * 4
      r = palette[i]
      g = palette[i + 1]
      b = palette[i + 2]
    } else [r, g, b] = changeColour(tv, colours)
    out[k * 4] = r
    out[k * 4 + 1] = g
    out[k * 4 + 2] = b
    out[k * 4 + 3] = alpha
  }
  return out
}

/** AYNI: what the change from a period's climatology needs (`climatology.ts`). */
export interface PeriodChangeInput {
  readonly now: LumaSnapshot
  readonly nowScale: ColorScale
  readonly clim: Climatology
  /** The calendar month (or 0) of the frame on screen. */
  readonly group: number
  readonly options?: DatasetOverlayOptions
  readonly mode: ChangeMode
  /** One quantisation step, or null where steps are not even (log scales). */
  readonly step: number | null
  readonly width?: number
  readonly height?: number
  readonly limit?: number | null
  /** AYNI: the viewer's own range; null for ±limit. */
  readonly range?: ChangeRange | null
  readonly floor?: number | null
}

/**
 * AYNI: the change from a period's climatology on the global grid: each
 * texel against the mean (and σ) of its own calendar month, by
 * `periodChange`'s rules. The % floor is set over the means of every month,
 * so it is one threshold for the whole baseline; σ change has the fixed
 * range ±{@link SIGMA_LIMIT}. Values are read as float32, as the graphics
 * card reads them, so the paused picture and the live one agree. Null when
 * the frame is on another grid.
 */
export function computePeriodChangeGrid(input: PeriodChangeInput): ChangeGrid | null {
  const { now, clim } = input
  if (now.width !== clim.width || now.height !== clim.height || now.width === 0 || now.height === 0) return null
  const width = input.width ?? CHANGE_GRID_WIDTH
  const height = input.height ?? CHANGE_GRID_HEIGHT
  const { cols, rows } = gridTexels(now.width, now.height, input.options, width, height)
  const nowValues = valueTable(input.nowScale)
  let floor: number | null = input.floor ?? null
  if (input.mode === 'percent' && floor === null) floor = percentFloor(meanMagnitudes(clim), input.step)
  const layer = input.group * clim.cw * clim.ch
  const values = new Float32Array(width * height).fill(NaN)
  const grey = new Uint8Array(width * height)
  let masked = 0
  let painted = 0
  let negative = 0
  for (let j = 0; j < height; j++) {
    const y = rows[j]
    if (y < 0) continue
    for (let i = 0; i < width; i++) {
      const x = cols[i]
      if (x < 0) continue
      const k = (layer + cellOf(clim, x, y)) * 2
      const mean = Math.fround(decodeMean(clim, clim.q[k]))
      const sigma = Math.fround(decodeSigma(clim, clim.q[k], clim.q[k + 1]))
      // A thinned climatology: the frame is read at the texel its cell was read at.
      const at = sampledTexel(clim, x, y)
      const a = Math.fround(nowValues[now.data[at.y * now.width + at.x]])
      const v = periodChange(a, mean, sigma, input.mode, floor)
      if (v === null) continue
      if (input.mode === 'percent' && Number.isFinite(mean) && Number.isFinite(a)) {
        if (mean < 0) negative++
        if (v === 'grey') masked++
      }
      if (v === 'grey') {
        grey[j * width + i] = 1
        continue
      }
      values[j * width + i] = v
      painted++
    }
  }
  let limit = input.limit !== undefined && input.limit !== null && input.limit > 0 ? input.limit : null
  if (limit === null) {
    if (input.mode === 'sigma') limit = SIGMA_LIMIT
    else {
      const magnitudes = new Float32Array(values.length)
      for (let k = 0; k < values.length; k++) magnitudes[k] = Math.abs(values[k])
      limit = niceLimit(quantile(magnitudes, 0.98))
    }
  }
  return { width, height, values, grey, mode: input.mode, limit, range: rangeOf(limit, input.range), masked, floor: input.mode === 'percent' ? floor : null, painted, negative }
}

/**
 * The change as a 256 × 256 colour table for the graphics card: the cell
 * at (now code, baseline code) holds the colour the map paints for that
 * pair, by the same rules as {@link paintChangeGrid} (palette within the
 * fixed range, grey for one side without data or a near-zero baseline in
 * percent mode, transparent where neither has data). The globe's shader
 * then needs two reads per pixel and nothing else, every frame. Row =
 * baseline code, column = now code; RGBA.
 */
export function buildChangeLut(
  nowScale: ColorScale,
  baseScale: ColorScale,
  mode: ChangeMode,
  floor: number | null,
  limit: number | ChangeRange,
  alpha = 255,
  colours: ChangeColours = DEFAULT_CHANGE_COLOURS,
): Uint8Array {
  const now = valueTable(nowScale)
  const base = valueTable(baseScale)
  const out = new Uint8Array(256 * 256 * 4)
  for (let b = 0; b < 256; b++) {
    const bv = base[b]
    for (let n = 0; n < 256; n++) {
      const nv = now[n]
      const k = (b * 256 + n) * 4
      const finiteN = Number.isFinite(nv)
      const finiteB = Number.isFinite(bv)
      if (!finiteN && !finiteB) continue
      let colour: readonly [number, number, number]
      if (finiteN !== finiteB) colour = CHANGE_GREY
      else if (mode === 'percent' && (Math.abs(bv) < (floor ?? 0) || bv === 0)) colour = CHANGE_GREY
      else {
        const v = mode === 'percent' ? (100 * (nv - bv)) / Math.abs(bv) : nv - bv
        colour = changeColour(changeT(v, limit), colours)
      }
      out[k] = colour[0]
      out[k + 1] = colour[1]
      out[k + 2] = colour[2]
      out[k + 3] = alpha
    }
  }
  return out
}

